import { isMobile } from 'react-device-detect';
import { observer } from 'mobx-react-lite';
import { useData } from '@microsoft/teamsfx-react';
import { Spinner } from '@fluentui/react-components';

import EnhancedExternalLink from 'src/components/atoms/enhanced-external-link/EnhancedExternalLink';
import WidgetContainer from 'src/components/molecules/widget-container/WidgetContainer';
import { useStore } from 'src/hooks/useStore';
import { HealthTracker } from 'src/models/healthTrackers/HealthTracker';
import EmptyStateMessage from 'src/components/molecules/empty-state-message/EmptyStateMessage';
import { ERROR_MESSAGES, TRACKERS_CONFIG } from 'src/utils/constants';
import HealthTrackerCard from 'src/components/molecules/health-tracker-card/HealthTrackerCard';
import EnhancedButton from 'src/components/atoms/enhanced-button/EnhancedButton';
import { openExternalUrl } from 'src/utils/utils';
import { useHealthTrackerStyles } from './HealthTrackersSection.styles';

const externalLink = 'https://www.google.com/';

const HealthTrackersSection: React.FC = () => {
  const styles = useHealthTrackerStyles();
  const { healthTrackersStore } = useStore();
  const { loading, error } = useData(healthTrackersStore.getHealthTrackers);
  const gridClass = isMobile ? styles.gridMobile : styles.grid;

  if (error) {
    throw error;
  }

  if (loading) {
    return <Spinner aria-label="Loading health tracker cards" />;
  }

  const healthTrackers: HealthTracker[] = healthTrackersStore.healthTrackers ?? [];
  if (healthTrackers.length === 0) {
    const actionElement = (
      <EnhancedButton onClick={() => openExternalUrl(externalLink)} isExternal>
        Pin Trackers
      </EnhancedButton>
    );
    return (
      <WidgetContainer title="Health Trackers">
        <EmptyStateMessage
          message="You do not have any Health Trackers pinned."
          actionElement={actionElement}
        />
      </WidgetContainer>
    );
  }

  const renderHealthTrackers = (): React.ReactNode => {
    return healthTrackers
      .filter(tracker => tracker.entries.length > 0)
      .map((healthTracker: HealthTracker) => {
        if (!(healthTracker.id in TRACKERS_CONFIG)) {
          throw new Error(ERROR_MESSAGES.invalidHealthTrackerType(healthTracker.id));
        }

        const { icon, color } = TRACKERS_CONFIG[healthTracker.id as keyof typeof TRACKERS_CONFIG];

        return <HealthTrackerCard key={healthTracker.id} healthTracker={healthTracker} icon={icon} color={color} />;
      });
  };

  const footer = <EnhancedExternalLink href={externalLink}>View trackers</EnhancedExternalLink>;

  return (
    <WidgetContainer title="Health Tracker" footer={footer}>
      <div className={gridClass}>{renderHealthTrackers()}</div>
    </WidgetContainer>
  );
};

export default observer(HealthTrackersSection);
